// ─── Proficiency scale ────────────────────────────────────────────────────────
// Matches SkillsBridger's own scale (EmployeeSkillsBridgerV2.constants.js) so
// Talent Mobility's skill-gap views are numerically consistent with SkillsBridger.

export const MIN_PROFICIENCY = 0;
export const MAX_PROFICIENCY = 5;

// Mirrors the footer legend on EmployeeSkillsBridgerV2.jsx (`scaleData.proficiency_scale`,
// normally fetched from the backend) — hardcoded here since there's no Talent
// Mobility backend to source it from yet.
export const PROFICIENCY_SCALE = [
  { level: 0, name: "No Experience" },
  { level: 1, name: "Beginner" },
  { level: 2, name: "Developing" },
  { level: 3, name: "Proficient" },
  { level: 4, name: "Advanced" },
  { level: 5, name: "Expert" },
];

// ─── Match / readiness thresholds (admin-configurable defaults) ──────────────
// These back Utils/scoreColor.utils.js and the org-view "Ready for Next Role"
// flag. Real values will eventually come from the Talent Mobility thresholds
// settings card in Admin Setup — these are the fallback/seed defaults.

export const DEFAULT_MATCH_THRESHOLDS = {
  green: 80, // match % at/above this renders green
  red: 50,   // match % below this always renders red (amber in between)
};

export const DEFAULT_SUCCESSION_READY_THRESHOLD = 90; // SRI % at/above this = "Ready Now"

// ─── Successor slider ─────────────────────────────────────────────────────────

export const SUCCESSOR_TOP_N_OPTIONS = [3, 5, 10];
export const SUCCESSOR_TOP_N_DEFAULT = 3;

// ─── Index / score labels (leadership-aligned terminology) ───────────────────
// Same underlying match formula, labeled differently by context: SSI when a
// person is evaluated as a succession candidate for a different role
// (Succession Planning / Role Detail), SMI in mobility contexts (Redeployment
// Center, Career Pathways, Workforce Planning). SRI is unchanged — a person's
// own fit for their current role. "Succession Score" is the separate Engine D
// weighted-aggregate metric, not a per-candidate score.
export const INDEX_LABELS = {
  SRI: "SRI",
  SSI: "SSI",
  SMI: "SMI",
  SUCCESSION_SCORE: "Succession Score",
};

// Full names behind the acronyms — surfaced as tooltips wherever an acronym
// renders (via components/IndexTooltip.jsx), so the terminology explains
// itself in place.
export const INDEX_FULL_NAMES = {
  SRI: "Skill Readiness Index",
  SSI: "Skill Succession Index",
  SMI: "Skill Match Index",
};

// ─── Text ─────────────────────────────────────────────────────────────────────

export const TEXT = {
  PAGE_TITLE: "Talent Mobility",
  PAGE_DESCRIPTION: [
    "Match internal talent to evolving roles and new opportunities.",
    "Talent Mobility uses the Skillsbridge Readiness and Mobility index to recommend potential successors and build scenario plans for the future.",
  ],
  BTN_PLANNING_TOOLS: "Planning Tools",
  BTN_ALL: "All",
  BTN_APPLICATIONS: "Applicants",
  BTN_SUCCESSION_PLANNING: "Succession Planning",
  BTN_REDEPLOYMENT_CENTER: "Redeployment Center",
  BTN_WORKFORCE_PLANNING: "Workforce Planning",
  BTN_SIMULATE_PAIC: "PAIC Org Redesign",
  BTN_VIEW_PLANNING: "View Plans",
  BACK_TO_ROLES: "Back to Roles",
  BACK_TO_TALENT_MOBILITY: "Back to Talent Mobility",
  BACK_TO_PATHWAYS: "Back to Career Pathways",
  SCOPE_TOGGLE_LABEL: "View scope",
  SCOPE_FULL_ORG: "Full Org (HR Manager)",
  SCOPE_MY_TEAM: "My Team Only (Line Manager)",
  SCALE_HEADING: "Skills Proficiency Scale",
  SCALE_SEPARATOR: " : ",
};

// ─── Mock scope values ────────────────────────────────────────────────────────
// There is no live Talent Mobility backend/RBAC yet (see plan). Scope defaults
// from the real logged-in profileRole (see useScopeToggle in
// useTalentMobilityData.js) with a manual override available — replace with
// real server-side scoping (mirroring PAIC's email-based resolution) once a
// Talent Mobility API exists.
export const SCOPE_FULL_ORG = "full_org";
export const SCOPE_MY_TEAM = "my_team";

// ─── Job opening status (job_desc.job_opening_status) ────────────────────────
// Mirrors the backend's JobOpeningStatus enum (app/enums/job_opening_status.py)
// — BigQuery has no ENUM/CHECK-constraint support, so the column is a plain
// STRING on the wire; these are the only two values that enum allows.
export const JOB_OPENING_STATUS = {
  OPEN: "open",
  CLOSED: "close",
};

// Labels the Open/Closed column (TalentMobilityManagerV2.jsx) renders,
// filters, and checks against — a single source of truth instead of
// hardcoding "Open"/"Closed" in both the column definition and the row mapper.
export const ROLE_STATUS_OPEN = "Open";
export const ROLE_STATUS_CLOSED = "Closed";

// ─── Nested table sizing ──────────────────────────────────────────────────────

// A role/person accordion panel's nested table (TalentMobilityRoleEmployeesTable,
// RedeploymentNestedTables' "next roles" table) has no pagination and no row
// cap, so a group with many rows would otherwise render every one inline and
// balloon the outer page's own height. Bounding it to this fixed height gives
// the nested table its own scrollbar instead.
export const NESTED_TABLE_SCROLL_HEIGHT = 320;
